/**
 * Keeps product-list carousels parked on their first item across page loads.
 *
 * A product list rendered as a carousel is a horizontally scrolling, snap-aligned
 * container. Browsers restore the scroll offset of such containers on reload and on
 * bfcache restore — most aggressively iOS Safari — so the row came back sitting on a
 * different product and the visible product images appeared to shuffle between
 * refreshes. The markup is identical on every load; only the scroll offset differs.
 *
 * Two levels can hold an offset, and both are reset:
 *   - `.resource-list__carousel` — the row itself, which product is shown
 *   - `.card-gallery`            — a single card, which image of that product is shown
 *
 * Scoped deliberately to those two. The product media gallery on a product page must
 * keep its restored position, because a variant selection legitimately parks it on a
 * non-first image.
 *
 * Strategy, in order of reliability:
 *
 *   1. `pagehide` / `visibilitychange` — zero the offsets *before* the page is frozen,
 *      so there is nothing left for the browser to restore. This is the load-bearing
 *      one: it wins regardless of when restoration runs on the way back in.
 *   2. `pageshow` — belt and braces for the fresh-load case, where nothing was frozen.
 *      iOS Safari applies its restore after a variable number of frames, so a single
 *      reset can be overwritten; retry briefly instead of assuming one pass is enough.
 */
const SCROLLER_SELECTOR = '.resource-list__carousel [ref="scroller"], .card-gallery [ref="scroller"]';

/** How long to keep re-asserting the reset after a restore, in ms. */
const RETRY_WINDOW_MS = 600;

/** Scrolls every product-list carousel back to its first item. */
function resetResourceListScroll() {
  for (const scroller of document.querySelectorAll(SCROLLER_SELECTOR)) {
    if (scroller.scrollLeft === 0) continue;
    // 'instant' overrides the CSS scroll-behavior on the snap container, so the reset
    // is never animated in front of the customer.
    scroller.scrollTo({ left: 0, behavior: 'instant' });
  }
}

/**
 * Re-asserts the reset for a short window, because the browser's own restoration can
 * land a few frames after `pageshow` and would otherwise overwrite a single reset.
 *
 * Uses timers as well as frames: `requestAnimationFrame` does not fire in a background
 * or hidden tab, which is exactly when a bfcache restore is about to happen, so a
 * frame-only retry can never run.
 */
function resetRepeatedly() {
  const deadline = performance.now() + RETRY_WINDOW_MS;

  // Two independent chains. They must not re-schedule each other: a single tick that
  // queued both a frame and a timer would double the callbacks every generation.
  const byFrame = () => {
    resetResourceListScroll();
    if (performance.now() < deadline) requestAnimationFrame(byFrame);
  };

  const byTimer = () => {
    resetResourceListScroll();
    if (performance.now() < deadline) setTimeout(byTimer, 50);
  };

  byFrame();
  byTimer();
}

// Leave nothing to restore.
window.addEventListener('pagehide', resetResourceListScroll);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') resetResourceListScroll();
});

// Fresh loads, and anything the pagehide pass did not catch.
window.addEventListener('pageshow', resetRepeatedly);
